import React, { useRef, useState } from "react"
import {SUFRow } from "../../services/ApiService"
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Chip,
  Stack,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Box,
  TableContainer,
  Tooltip
} from "@mui/material"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import moment from "moment"
import { messageStore } from "../../app/common/messages/MessageStore"
import RosterService, { CSVResult } from "./RosteringService"
import { ADMIN_CREATE_CODE_AND_ROSTER } from "../../util/Constants"
import { authStore } from "../../app/common/authentication/AuthStore"


type RosterErrors = {
  firstName?: boolean
  lastName?: boolean
  grade?: boolean
  dateOfBirth?: boolean
  districtCode?: boolean
  districtName?: boolean
  schoolCode?: boolean
  schoolName?: boolean
  stateId?: boolean
}

// ----- VALIDATION HELPERS -----
const reDistrictCode = /^[A-Za-z]{2}\d{3}$/ // 2 letters + 3 digits
const reAlnumName = /^[A-Za-z0-9 .,'-]+$/ // alphanumeric + space + .,'-
const reStudentName = /^[\p{L}][\p{L} .'-]*$/u
const validGrades = new Set([
  "K",
  "1","2","3","4","5","6","7","8","9","10","11","12",
  "01","02","03","04","05","06","07","08","09"
])
const reStateId9 = /^\d{9}$/

const fmt = (d?: string | number) =>
  d ? moment(d).isValid() ? moment(d).format("MM/DD/YYYY") : String(d) : ""

const RosterAndCodeCreation = () => {
  const [file, setFile] = useState<File | null>(null)
  const [results, setResults] = useState<CSVResult[]>([])
  const [updateResults, setUpdateResults] = useState<CSVResult[]>([])
  const [codeCount, setCodeCount] = useState<number>(0)
  const [loading, setLoading] = useState(false)
  const [hideResultsAfterSuccess, setHideResultsAfterSuccess] = useState(false)
  const [downloadEnabled, setDownloadEnabled] = useState(false)

  const inputRef = useRef<HTMLInputElement>(null)

  const resetAll = () => {
    if (inputRef.current) inputRef.current.value = ""
    setFile(null)
    setResults([])
    setCodeCount(0)
    setDownloadEnabled(false)
  }

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) {
      const f = e.target.files[0]
      if (!f.name.toLowerCase().endsWith(".csv")) {
        alert("Please upload a CSV file")
        resetAll()
        return
      }
      setFile(f)
    }
  }

  const uploadAndCreate = async () => {
    if (!file) {
      messageStore.setInfoMessage("Please select a CSV file")
      return
    }

    try {
      setLoading(true)
      setHideResultsAfterSuccess(false)
      const validated: CSVResult[] = await RosterService.uploadAndValidateCSV(file)

      const firstErrorGroup = validated.find(g => g.error !== null)
      if (firstErrorGroup) {
        messageStore.setErrorMessage(firstErrorGroup.error!)
      }

      setResults(validated)
      const totalCodes = validated.reduce((sum, sr) => sum + (sr.sufRows?.length || 0), 0)
      setCodeCount(totalCodes)

      if (validated){
    const updatedResults: CSVResult[] = validated.map(group => {
      const updatedSufRows = (group.sufRows || []).map(r => {
        const newSchool = r.school ? r.school : ""
        const newClass = r.classOrLocation ? r.classOrLocation : ""
        const combined = `${newSchool} - ${newClass}`

        return {
          ...r,
          school: group.districtOrCustomer,
          classOrLocation: combined,
        }
      })

      return {
        ...group,
        districtOrCustomer: group.state,
        sufRows: updatedSufRows,
      }
    })
    setUpdateResults(updatedResults)

      }
    } catch (err) {
      messageStore.setErrorMessage(`${err}`)
    } finally {
      setLoading(false)
    }
  }

  const uploadAndCreateWithRoster = async () => {
    try {
      setLoading(true)
      setHideResultsAfterSuccess(true)
      const created: CSVResult[] = await RosterService.submitSUFToCreateCodesAndRoster(updateResults)
      const firstErrorGroup = created.find(g => g.error !== null)
      if (firstErrorGroup) {
        messageStore.setErrorMessage(firstErrorGroup.error!)
      }
        // 🔹 Collect all rosterTesttakers across groups
      const allRosterTestTakers = created.flatMap(g => g.rosterTesttakers)

      // 🔹 Example: find first test-taker with error
      const firstErroredTaker = allRosterTestTakers.find(r => r.error !== null)
      if (firstErroredTaker) {
        console.error("First roster error:", firstErroredTaker.error)
      }

      if (allRosterTestTakers.every(r => r.error === null)) {
        setHideResultsAfterSuccess(true)
        messageStore.setInfoMessage("Codes created, roster processed, and profiles updated successfully.")
      } 
      else if (allRosterTestTakers.every(r => r.error !== null)) {
        setHideResultsAfterSuccess(false)
        messageStore.setErrorMessage("Roster testtakers already exist in the system.")
      } 
      else {
        setHideResultsAfterSuccess(false)
        messageStore.setErrorMessage("Roster testtakers already exist in the system.")
      }
      setResults(created)
      setDownloadEnabled(true)
    } catch (err) {
      messageStore.setErrorMessage(`Failed to create codes and process roster: ${err instanceof Error ? err.message : String(err)}`)
    } finally {
      setLoading(false)
    }
  }

  const downloadCodesCsv = () => {
    if (!results.length) return

    const headers =
      "State,Organization,Sub-Organization,Testing Group,Language,Open Date,Close Date,Test Code,Password,User Type,Teachers Email\n"

    const csvLines: string[] = []
    csvLines.push(headers)

    results.forEach(group => {
      (group.sufRows || []).forEach((r: SUFRow) => {
        if (r.success && r.student && r.teacher) {
          csvLines.push(
            `"${group.state}","${group.districtOrCustomer}","${r.school ?? ""}","${r.classOrLocation ?? ""}","${r.language ?? ""}","${fmt(r.openDate)}","${fmt(r.closeDate)}","${r.student.username}","${r.student.pw}","Test Takers","${group.teacherEmail}"\n`
          )
          csvLines.push(
            `,,,,,,,"${r.teacher.username}","${r.teacher.pw}","Proctor/Results Login"\n`
          )
        }
      })
    })

    const blob = new Blob(csvLines, { type: "text/csv;charset=utf-8;" })
    const a = document.createElement("a")
    a.href = URL.createObjectURL(blob)
    a.download = "Codes.csv"
    a.click()
  }

const validateRosterRow = (t: any): RosterErrors => {
  const errs: RosterErrors = {}

  //  Skip validation if state is Utah (case-insensitive)
  if (t?.state && String(t.state).trim().toLowerCase() === "utah") {
    return errs
  }

  // --- schoolCode fix with leading zeros ---
  let schoolCode = String(t?.schoolCode ?? "").trim()
  if (schoolCode && /^\d{1,3}$/.test(schoolCode)) {
    schoolCode = schoolCode.padStart(3, "0")
    t.schoolCode = schoolCode
  } else {
    errs.schoolCode = true
  }
  //  Otherwise run normal validations
  if (!t?.districtCode || !reDistrictCode.test(String(t.districtCode).trim())) errs.districtCode = true
  if (!t?.districtName || !reAlnumName.test(String(t.districtName).trim())) errs.districtName = true
  if (!t?.schoolName || !reAlnumName.test(String(t.schoolName).trim())) errs.schoolName = true
  if (!t?.stateId || !reStateId9.test(String(t.stateId).trim())) errs.stateId = true
  if (!t?.firstName || !reStudentName.test(String(t.firstName).trim())) errs.firstName = true
  if (!t?.lastName || !reStudentName.test(String(t.lastName).trim())) errs.lastName = true
  if (!t?.grade || !validGrades.has(String(t.grade).trim())) errs.grade = true
  if (!fmt(t?.dateOfBirth)) errs.dateOfBirth = true

  return errs
}

  const hasAnyError = (e: RosterErrors) => Object.values(e).some(Boolean)

  const invalidCellSx = {
    bgcolor: "rgba(244, 67, 54, 0.12)",
    color: "error.main",
  }

  const allSufRows = results.flatMap(group => group.sufRows || [])
  const allRosterTesttakers = results.flatMap(group => group.rosterTesttakers || [])
  const canRoster = authStore?.auth?.userPermissions?.includes(ADMIN_CREATE_CODE_AND_ROSTER) ?? false;
  return (
    <Box sx={{ maxWidth: 1100, mx: "auto", p: 2 }}>
      <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
        Roster & Code Creation
      </Typography>

      {!!codeCount && (
        <Typography variant="body1" sx={{ mb: 1 }}>
          Number of codes (est.): <b>{codeCount}</b>
        </Typography>
      )}

      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems="center" sx={{ mb: 2 }}>
        <input
          ref={inputRef}
          type="file"
          accept=".csv"
          onChange={onFileChange}
          style={{ marginBottom: 8 }}
        />

        <Button variant="contained" onClick={uploadAndCreate} disabled={!file || loading || !canRoster}>
          {loading ? "Processing..." : "Upload CSV"}
        </Button>

        <Button
          variant="contained"
          color="secondary"
          onClick={downloadCodesCsv}
          disabled={!downloadEnabled}
        >
          Download Codes
        </Button>

        <Button variant="outlined" onClick={resetAll}>
          Reset
        </Button>
      </Stack>

      {hideResultsAfterSuccess || results.every(r => r.error !== null) ? (
        <Paper sx={{ p: 2, borderRadius: 2, color: "text.secondary" }}>
          Upload a CSV to see results.
          <br />
          Permission required: <b>ADMIN_CREATE_CODE_AND_ROSTER</b>
        </Paper>
      ) : (
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ backgroundColor: "#f5f7fa" }}>
            <Stack spacing={0.5} sx={{ width: "100%" }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                All Results
              </Typography>
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                <Chip size="small" label={`Groups: ${results.length}`} />
                <Chip size="small" label={`Total Codes: ${allSufRows.length}`} />
                <Chip size="small" label={`Total TestTakers: ${allRosterTesttakers.length}`} />
                <Chip
                  size="small"
                  color={results.some(g => g.productId === -1 || g.sufRows[0]?.student === null) ? "warning" : "success"}
                  label={results.some(g => g.productId === -1 || g.sufRows[0]?.student === null) ? "Invalid" : "All Valid"}
                />
              </Stack>
              {results.some(g => g.error) && (
                <Typography variant="caption" color="error">
                  {results.filter(g => g.error).map((g, i) =>
                    <span key={i}>{g.error}{i < results.length - 1 ? ", " : ""}</span>
                  )}
                </Typography>
              )}
            </Stack>
          </AccordionSummary>

          <AccordionDetails>
            <Stack spacing={2}>
              {/* SUF ROWS */}
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  Test Code
                </Typography>
                {!allSufRows.length ? (
                  <Typography variant="body2" color="text.secondary">No SUF rows</Typography>
                ) : (
                  <TableContainer component={Paper} sx={{ maxHeight: 420}}>
                    <Table size="small" stickyHeader sx={{ minWidth: 1200 }}>
                      <TableHead>
                        <TableRow>
                          <TableCell>#</TableCell>
                          <TableCell>Language</TableCell>
                          <TableCell>School/Customer</TableCell>
                          <TableCell>Class/Location</TableCell>
                          <TableCell>Open Date</TableCell>
                          <TableCell>Close Date</TableCell>
                          <TableCell>Student Code</TableCell>
                          <TableCell>Teacher Code</TableCell>
                          <TableCell>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {allSufRows.map((r, i) => (
                          <TableRow key={i} sx={!r.success ? { bgcolor: "rgba(244, 67, 54, 0.12)" } : {}}>
                            <TableCell>{r.line}</TableCell>
                            <TableCell>{r.language ?? "-"}</TableCell>
                            <TableCell>{r.school ?? "-"}</TableCell>
                            <TableCell>{r.classOrLocation ?? "-"}</TableCell>
                            <TableCell>{fmt(r.openDate)}</TableCell>
                            <TableCell>{fmt(r.closeDate)}</TableCell>
                            <TableCell>{r.student?.username ?? "-"}</TableCell>
                            <TableCell>{r.teacher?.username ?? "-"}</TableCell>
                            <TableCell>
                              {r.success ? (
                                <Chip size="small" color="success" label="OK" />
                              ) : (
                                <Chip size="small" color="warning" label="Invalid Product Package / Language" />
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>

              {/* ROSTER TESTTAKERS */}
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  Roster Test Takers
                </Typography>
                {!allRosterTesttakers.length ? (
                  <Typography variant="body2" color="text.secondary">No roster rows</Typography>
                ) : (
                  <TableContainer component={Paper} sx={{ maxHeight: 420}}>
                    <Table size="small" stickyHeader sx={{ minWidth: 1200 }}>
                    <TableHead>
          <TableRow>
          <TableCell sx={{ minWidth: 90 }}>Status</TableCell>
            <TableCell sx={{ minWidth: 120 }}>
              <Tooltip title="Student first name. Letters, spaces, periods, apostrophes, and hyphens allowed."><span>First Name</span></Tooltip>
            </TableCell>
            <TableCell sx={{ minWidth: 120 }}>
              <Tooltip title="Student first name. Letters, spaces, periods, apostrophes, and hyphens allowed."><span>Last Name</span></Tooltip>
            </TableCell>
            <TableCell sx={{ minWidth: 80 }}>
              <Tooltip title="Valid Grades: K, 1–12 (with or without leading 0)."><span>Grade</span></Tooltip>
            </TableCell>
            <TableCell sx={{ minWidth: 90 }}>
              <Tooltip title="Gender of student (M/F or other accepted values)."><span>Gender</span></Tooltip>
            </TableCell>
            <TableCell sx={{ minWidth: 110 }}>
              <Tooltip title="Birthdate in MM/DD/YYYY format."><span>DOB</span></Tooltip>
            </TableCell>
            <TableCell sx={{ minWidth: 120 }}>
              <Tooltip title="Language of test taker."><span>Language</span></Tooltip>
            </TableCell>
            <TableCell sx={{ minWidth: 120 }}>
              <Tooltip title="Two-character state code."><span>State</span></Tooltip>
            </TableCell>
            <TableCell sx={{ minWidth: 160 }}>
              <Tooltip title="District name (letters and/or numbers allowed)."><span>District</span></Tooltip>
            </TableCell>
            <TableCell sx={{ minWidth: 160 }}>
              <Tooltip title="District Code: 2-letter state code + 3 digits."><span>District Code</span></Tooltip>
            </TableCell>
            <TableCell sx={{ minWidth: 160 }}>
              <Tooltip title="School name (letters and/or numbers allowed)."><span>School</span></Tooltip>
            </TableCell>
            <TableCell sx={{ minWidth: 160 }}>
              <Tooltip title="School Code: 3-digit code unique within district."><span>School Code</span></Tooltip>
            </TableCell>
            <TableCell sx={{ minWidth: 160 }}>
              <Tooltip title="Testing group name (class or location)."><span>Testing Group</span></Tooltip>
            </TableCell>
            <TableCell sx={{ minWidth: 110 }}>
              <Tooltip title="Test access open date (MM/DD/YYYY)."><span>Open</span></Tooltip>
            </TableCell>
            <TableCell sx={{ minWidth: 110 }}>
              <Tooltip title="Test access close date (MM/DD/YYYY)."><span>Close</span></Tooltip>
            </TableCell>
            <TableCell sx={{ minWidth: 120 }}>
              <Tooltip title="Salesforce ID (system identifier)."><span>SF ID</span></Tooltip>
            </TableCell>
            <TableCell sx={{ minWidth: 120 }}>
              <Tooltip title="State Student ID: 9-digit numeric value."><span>SSID</span></Tooltip>
            </TableCell>
            <TableCell sx={{ minWidth: 130 }}>
              <Tooltip title="Product package code."><span>Product Package</span></Tooltip>
            </TableCell>
          </TableRow>
        </TableHead>
                      <TableBody>
                        {allRosterTesttakers.map((t, i) => {
                          const errs = validateRosterRow(t)
                          const hasError = hasAnyError(errs)
                          return (
                            <TableRow key={i} sx={t.error ? { bgcolor: "rgba(244, 67, 54, 0.12)" } : {}}>
                                 <TableCell>
                                  {hasError || t.error !== null? (
                                    <Chip size="small" color="warning" label="Error" />
                                  ) : (
                                    <Chip size="small" color="success" label="OK" />
                                  )}
                                </TableCell>
                              <TableCell sx={errs.firstName ? invalidCellSx : undefined}>{t.firstName || "-"}</TableCell>
                              <TableCell sx={errs.lastName ? invalidCellSx : undefined}>{t.lastName || "-"}</TableCell>
                              <TableCell sx={errs.grade ? invalidCellSx : undefined}>{t.grade || "-"}</TableCell>
                              <TableCell>{t.gender || "-"}</TableCell>
                              <TableCell sx={errs.dateOfBirth ? invalidCellSx : undefined}>{fmt(t.dateOfBirth)}</TableCell>
                              <TableCell>{t.language || "-"}</TableCell>
                              <TableCell>{t.state || "-"}</TableCell>
                              <TableCell sx={errs.districtName ? invalidCellSx : undefined}>{t.districtName || "-"}</TableCell>
                              <TableCell sx={errs.districtCode ? invalidCellSx : undefined}>{t.districtCode || "-"}</TableCell>
                              <TableCell sx={errs.schoolName ? invalidCellSx : undefined}>{t.schoolName || "-"}</TableCell>
                              <TableCell sx={errs.schoolCode ? invalidCellSx : undefined}>{t.schoolCode || "-"}</TableCell>
                              <TableCell>{t.testingGroupName || "-"}</TableCell>
                              <TableCell>{fmt(t.openDate)}</TableCell>
                              <TableCell>{fmt(t.closeDate)}</TableCell>
                              <TableCell>{t.sfId || "-"}</TableCell>
                              <TableCell sx={errs.stateId ? invalidCellSx : undefined}>{t.stateId || "-"}</TableCell>
                              <TableCell>{t.productPackage || "-"}</TableCell>
                            </TableRow>
                          )
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            </Stack>

            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
              <Button
                variant="contained"
                color="primary"
                disabled={allRosterTesttakers.some(t => hasAnyError(validateRosterRow(t))) || allSufRows.some(r => r.student === null) || results.some(r => r.error !== null) || allRosterTesttakers.some(t => t.error !== null) || allSufRows.length === 0} 
                title={allRosterTesttakers.some(t => hasAnyError(validateRosterRow(t))) ? "Fix highlighted fields to proceed" : ""}
                onClick={uploadAndCreateWithRoster}
              >
                Create Code and Roster
              </Button>
            </Box>
          </AccordionDetails>
        </Accordion>
      )}
    </Box>
  )
}

export default RosterAndCodeCreation
